import dayjs from "dayjs";
import { zSchema } from "../../schema-validation";
import { ECitizenshipStatus } from "../ECitizenshipStatus";
import { EDocumentField } from "../EDocumentField";
import { EDocumentKey } from "../EDocumentKey";
import { TDocumentTypeZodSchemaMap } from "./document-config";

/**
 * Zod schema for a nullable expiration date field.
 * Different from the required expiration date field on purpose.
 * Zod refinements do not work as expected with null fields if
 * the refinements run before the nullish() validator.
 */
const nullableExpirationDateZodSchema = zSchema
  .string()
  .nullish()
  .refine(
    (dateString) => {
      if (!dateString) {
        // Skip validation if dateString is null or undefined
        return true;
      }

      const date = dayjs(dateString, "YYYY-MM-DD", true);

      // Check if the date is in the future
      return date.isAfter(dayjs());
    },
    {
      message: "La fecha de expiración debe estar en el futuro.",
    }
  );

export const documentFieldZodSchemaMapSpanish: Record<
  EDocumentField,
  zSchema.ZodSchema<any, any>
> = {
  [EDocumentField.US_PASSPORT_NUMBER]: zSchema
    .string({ required_error: "Se requiere el número de pasaporte de EE.UU." })
    .regex(/^[A-Za-z0-9]*$/, "Número de pasaporte de EE.UU. inválido.")
    .min(6, {
      message:
        "El número de pasaporte de EE.UU. debe tener al menos 6 caracteres.",
    })
    .max(9, {
      message:
        "El número de pasaporte de EE.UU. debe tener como máximo 9 caracteres.",
    }),
  [EDocumentField.EXPIRATION_DATE]: zSchema
    .string({ required_error: "Requerido" })
    .refine(
      (dateString) => {
        const date = dayjs(dateString, "YYYY-MM-DD", true);

        // Check if the date is in the future
        return date.isAfter(dayjs());
      },
      {
        message: "La fecha de expiración debe estar en el futuro.",
      }
    ),
  [EDocumentField.ISSUING_AUTHORITY]: zSchema
    .string({
      required_error: "Se requiere la autoridad emisora.",
    })
    .trim()
    .min(1, "Se requiere la autoridad emisora."),
  [EDocumentField.DOCUMENT_NUMBER]: zSchema.string().optional(),
  [EDocumentField.SSN_NUMBER]: zSchema.string(),
  [EDocumentField.DOCUMENT_BC_NUMBER]: zSchema
    .string({
      required_error:
        "Se requiere el número de licencia de conducir / identificación estatal.",
    })
    .trim()
    .max(17, {
      message: "El número de documento debe tener como máximo 17 caracteres.",
    })
    .regex(
      /^[a-zA-Z0-9*-]*$/,
      "Número de documento inválido. Solo se permiten caracteres alfanuméricos, asteriscos y guiones."
    ),
  [EDocumentField.ALIEN_NUMBER]: zSchema
    .string({
      required_error: "Se requiere el número USCIS o A.",
    })
    .regex(
      /^A\d{8,9}$/,
      'Número USCIS/A inválido. Debe ser la letra "A" seguida de 8 o 9 dígitos. No debe contener espacios ni caracteres especiales. Ej: A123456789.'
    ),
  [EDocumentField.FOREIGN_PASSPORT_NUMBER]: zSchema
    .string({
      required_error: "Se requiere el número de pasaporte extranjero.",
    })
    .max(12, {
      message:
        "El número de pasaporte extranjero debe tener como máximo 12 caracteres.",
    })
    .regex(/^[A-Za-z0-9]{1,12}$/, "Número de pasaporte extranjero inválido."),
  [EDocumentField.COUNTRY_CODE]: zSchema.string({
    required_error: "Por favor seleccione un país.",
  }),
  [EDocumentField.I551_NUMBER]: zSchema.string().regex(
    /^[a-zA-Z]{3}(\d{10}|[*]\d{9})$/,
    // Validation message coming directly from the e-verify web services document
    "El número I551 debe tener exactamente 13 caracteres comenzando con 3 letras."
  ),
  [EDocumentField.I766_NUMBER]: zSchema
    .string({
      required_error: "Se requiere el número I766.",
    })
    .regex(
      /^[a-zA-Z]{3}(\d{10}|[*]\d{9})$/,
      "Número I766 inválido. Debe tener exactamente 13 caracteres comenzando con 3 letras."
    )
    .length(13, {
      message: "El número I766 debe tener exactamente 13 caracteres.",
    }),
  [EDocumentField.I94_NUMBER]: zSchema
    .string({
      required_error: "Se requiere el número I94.",
    })
    .regex(
      /^[0-9A-Za-z]{11}$/,
      "El número I94 debe tener exactamente 11 caracteres."
    ),
  [EDocumentField.US_STATE_CODE]: zSchema.string({
    required_error: "Por favor seleccione un estado.",
  }),
  [EDocumentField.DOCUMENT_SUB_TYPE_CODE]: zSchema.string({
    required_error:
      "Por favor especifique el tipo de su licencia de conducir / identificación estatal.",
  }),
  [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER]: zSchema.enum(
    [EDocumentField.ALIEN_NUMBER, EDocumentField.I94_NUMBER],
    {
      required_error: "Se requiere el número de extranjero o el número I94.",
    }
  ),
  [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER_OR_FOREIGN_PASSPORT_NUMBER]:
    zSchema.enum(
      [
        EDocumentField.ALIEN_NUMBER,
        EDocumentField.I94_NUMBER,
        EDocumentField.FOREIGN_PASSPORT_NUMBER,
      ],
      {
        required_error:
          "Se requiere el número de extranjero, el número I94 o el número de pasaporte extranjero.",
      }
    ),
  [EDocumentField.SEVIS_NUMBER]: zSchema
    .string()
    .length(11, {
      message: "El número SEVIS debe tener exactamente 11 caracteres.",
    })
    .regex(
      /^N\d{10}$/,
      "El número SEVIS debe ser la letra N seguida de 10 números."
    )
    .optional(),
  [EDocumentField.VISA_NUMBER]: zSchema
    .string()
    .length(8, {
      message: "El número de visa debe tener exactamente 8 caracteres.",
    })
    .regex(/^[A-Za-z0-9]*$/, "El número de visa debe ser alfanumérico.")
    .optional(),
  [EDocumentField.STATUS]: zSchema.nativeEnum(ECitizenshipStatus, {
    required_error: "Elija un estado de ciudadanía o inmigración.",
  }),
};

const optionalSchema = Object.keys(documentFieldZodSchemaMapSpanish).reduce(
  (acc, key) => {
    // Object.keys() creates a list of strings, so we need to explicitly convert each key to the EDocumentField type
    const typedKey = key as EDocumentField;
    acc[typedKey] = documentFieldZodSchemaMapSpanish[typedKey].optional();
    return acc;
  },
  {} as typeof documentFieldZodSchemaMapSpanish
);

export const allKeysOptionalI9DocumentZodSchemaSpanish =
  zSchema.object(optionalSchema);

const alienOrNoncitizenAuthorizedToWorkCommonZodSchemaPairs = {
  [EDocumentField.STATUS]:
    documentFieldZodSchemaMapSpanish[EDocumentField.STATUS],
  [EDocumentField.EXPIRATION_DATE]: nullableExpirationDateZodSchema,
};

/**
 * ControlledInput fields are set to "" when cleared.
 * This function preprocesses the schema to convert "" to undefined
 * so that zod validation does not fail on empty strings.
 */
const preprocessNullishSchemas = (schema: zSchema.ZodSchema<any, any>) =>
  zSchema.preprocess((val) => (val === "" ? undefined : val), schema.nullish());

export const citizenshipStatusToZodSchemaMapSpanish = (
  require_i551_number: boolean
): Partial<Record<ECitizenshipStatus, zSchema.ZodSchema<any, any>>> => ({
  [ECitizenshipStatus.LAWFUL_PERMANENT_RESIDENT]: zSchema.object({
    [EDocumentField.ALIEN_NUMBER]:
      documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER],
    [EDocumentField.STATUS]: zSchema.nativeEnum(ECitizenshipStatus, {
      required_error: "Elija un estado de ciudadanía o inmigración.",
    }),
    ...(require_i551_number && {
      [EDocumentField.I551_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.I551_NUMBER],
    }),
  }),
  [ECitizenshipStatus.NONCITIZEN_AUTHORIZED_TO_WORK]:
    zSchema.discriminatedUnion(
      EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER_OR_FOREIGN_PASSPORT_NUMBER,
      [
        zSchema.object({
          ...alienOrNoncitizenAuthorizedToWorkCommonZodSchemaPairs,
          [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER_OR_FOREIGN_PASSPORT_NUMBER]:
            zSchema.enum([EDocumentField.ALIEN_NUMBER]),
          [EDocumentField.ALIEN_NUMBER]:
            documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER],
          [EDocumentField.I94_NUMBER]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[EDocumentField.I94_NUMBER]
          ),
          [EDocumentField.FOREIGN_PASSPORT_NUMBER]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[
              EDocumentField.FOREIGN_PASSPORT_NUMBER
            ]
          ),
          [EDocumentField.COUNTRY_CODE]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[EDocumentField.COUNTRY_CODE]
          ),
        }),
        zSchema.object({
          ...alienOrNoncitizenAuthorizedToWorkCommonZodSchemaPairs,
          [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER_OR_FOREIGN_PASSPORT_NUMBER]:
            zSchema.enum([EDocumentField.I94_NUMBER]),
          [EDocumentField.ALIEN_NUMBER]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER]
          ),
          [EDocumentField.I94_NUMBER]:
            documentFieldZodSchemaMapSpanish[EDocumentField.I94_NUMBER],
          [EDocumentField.FOREIGN_PASSPORT_NUMBER]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[
              EDocumentField.FOREIGN_PASSPORT_NUMBER
            ]
          ),
          [EDocumentField.COUNTRY_CODE]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[EDocumentField.COUNTRY_CODE]
          ),
        }),
        zSchema.object({
          ...alienOrNoncitizenAuthorizedToWorkCommonZodSchemaPairs,
          [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER_OR_FOREIGN_PASSPORT_NUMBER]:
            zSchema.enum([EDocumentField.FOREIGN_PASSPORT_NUMBER]),
          [EDocumentField.ALIEN_NUMBER]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER]
          ),
          [EDocumentField.I94_NUMBER]: preprocessNullishSchemas(
            documentFieldZodSchemaMapSpanish[EDocumentField.I94_NUMBER]
          ),
          [EDocumentField.FOREIGN_PASSPORT_NUMBER]:
            documentFieldZodSchemaMapSpanish[
              EDocumentField.FOREIGN_PASSPORT_NUMBER
            ],
          [EDocumentField.COUNTRY_CODE]:
            documentFieldZodSchemaMapSpanish[EDocumentField.COUNTRY_CODE],
        }),
      ]
    ),
});

export const citizenshipStatusIndependentDocumentTypeZodSchemaMapSpanish: Partial<
  Record<EDocumentKey, zSchema.ZodObject<any, any>>
> = {
  [EDocumentKey.DRIVERS_LICENSE]: zSchema.object({
    [EDocumentField.DOCUMENT_SUB_TYPE_CODE]:
      documentFieldZodSchemaMapSpanish[EDocumentField.DOCUMENT_SUB_TYPE_CODE],
    [EDocumentField.US_STATE_CODE]:
      documentFieldZodSchemaMapSpanish[EDocumentField.US_STATE_CODE],
    [EDocumentField.DOCUMENT_BC_NUMBER]:
      documentFieldZodSchemaMapSpanish[EDocumentField.DOCUMENT_BC_NUMBER],
  }),
};

type TCitizenshipStatusWithBaseSchema =
  ECitizenshipStatus.NONCITIZEN_AUTHORIZED_TO_WORK;

type TDocumentKeyWithBaseSchema = EDocumentKey.FOREIGN_PASSPORT_WITH_FORM_I94;

const documentTypeBaseZodSchemaMap: Record<
  TCitizenshipStatusWithBaseSchema,
  Partial<
    Record<
      TDocumentKeyWithBaseSchema,
      Partial<Record<EDocumentField, zSchema.ZodSchema<any, any>>>
    >
  >
> = {
  [ECitizenshipStatus.NONCITIZEN_AUTHORIZED_TO_WORK]: {
    [EDocumentKey.FOREIGN_PASSPORT_WITH_FORM_I94]: {
      [EDocumentField.FOREIGN_PASSPORT_NUMBER]:
        documentFieldZodSchemaMapSpanish[
          EDocumentField.FOREIGN_PASSPORT_NUMBER
        ],
      [EDocumentField.EXPIRATION_DATE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.EXPIRATION_DATE],
      [EDocumentField.COUNTRY_CODE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.COUNTRY_CODE],
      [EDocumentField.SEVIS_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.SEVIS_NUMBER],
      [EDocumentField.VISA_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.VISA_NUMBER],
    },
  },
};

const alienI94NumberConditionalSchema = zSchema.discriminatedUnion(
  EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER,
  [
    zSchema.object({
      [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER]: zSchema.literal(
        EDocumentField.ALIEN_NUMBER
      ),
      [EDocumentField.ALIEN_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER],
      [EDocumentField.I94_NUMBER]: preprocessNullishSchemas(
        documentFieldZodSchemaMapSpanish[EDocumentField.I94_NUMBER]
      ),
    }),
    zSchema.object({
      [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER]: zSchema.literal(
        EDocumentField.I94_NUMBER
      ),
      [EDocumentField.ALIEN_NUMBER]: preprocessNullishSchemas(
        documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER]
      ),
      [EDocumentField.I94_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.I94_NUMBER],
    }),
  ]
);

export const documentTypeZodSchemaMapSpanish: TDocumentTypeZodSchemaMap = {
  [ECitizenshipStatus.US_CITIZEN]: {
    [EDocumentKey.US_PASSPORT]: zSchema.object({
      [EDocumentField.US_PASSPORT_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.US_PASSPORT_NUMBER],
      [EDocumentField.EXPIRATION_DATE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.EXPIRATION_DATE],
    }),
  },
  [ECitizenshipStatus.NONCITIZEN]: {
    [EDocumentKey.US_PASSPORT]: zSchema.object({
      [EDocumentField.US_PASSPORT_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.US_PASSPORT_NUMBER],
      [EDocumentField.EXPIRATION_DATE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.EXPIRATION_DATE],
    }),
  },
  [ECitizenshipStatus.LAWFUL_PERMANENT_RESIDENT]: {
    [EDocumentKey.FOREIGN_PASSPORT_WITH_I551_STAMP]: zSchema.object({
      [EDocumentField.FOREIGN_PASSPORT_NUMBER]:
        documentFieldZodSchemaMapSpanish[
          EDocumentField.FOREIGN_PASSPORT_NUMBER
        ],
      [EDocumentField.EXPIRATION_DATE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.EXPIRATION_DATE],
      [EDocumentField.COUNTRY_CODE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.COUNTRY_CODE],
    }),
    [EDocumentKey.FORM_I94_RECEIPT]: zSchema.object({
      [EDocumentField.EXPIRATION_DATE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.EXPIRATION_DATE],
    }),
  },
  [ECitizenshipStatus.NONCITIZEN_AUTHORIZED_TO_WORK]: {
    [EDocumentKey.FOREIGN_PASSPORT_WITH_FORM_I94]: zSchema.discriminatedUnion(
      EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER,
      [
        zSchema.object({
          ...documentTypeBaseZodSchemaMap[
            ECitizenshipStatus.NONCITIZEN_AUTHORIZED_TO_WORK
          ][EDocumentKey.FOREIGN_PASSPORT_WITH_FORM_I94],
          [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER]: zSchema.literal(
            EDocumentField.ALIEN_NUMBER
          ),
          [EDocumentField.ALIEN_NUMBER]:
            documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER],
          [EDocumentField.I94_NUMBER]:
            documentFieldZodSchemaMapSpanish[
              EDocumentField.I94_NUMBER
            ].nullish(),
        }),
        zSchema.object({
          ...documentTypeBaseZodSchemaMap[
            ECitizenshipStatus.NONCITIZEN_AUTHORIZED_TO_WORK
          ][EDocumentKey.FOREIGN_PASSPORT_WITH_FORM_I94],
          [EDocumentField.ALIEN_NUMBER_OR_I94_NUMBER]: zSchema.literal(
            EDocumentField.I94_NUMBER
          ),
          [EDocumentField.ALIEN_NUMBER]:
            documentFieldZodSchemaMapSpanish[
              EDocumentField.ALIEN_NUMBER
            ].nullish(),
          [EDocumentField.I94_NUMBER]:
            documentFieldZodSchemaMapSpanish[EDocumentField.I94_NUMBER],
        }),
      ]
    ),
    [EDocumentKey.FORM_I94_RECEIPT]: alienI94NumberConditionalSchema,
    [EDocumentKey.FORM_I766]: zSchema.object({
      [EDocumentField.ALIEN_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.ALIEN_NUMBER],
      [EDocumentField.I766_NUMBER]:
        documentFieldZodSchemaMapSpanish[EDocumentField.I766_NUMBER],
      [EDocumentField.EXPIRATION_DATE]:
        documentFieldZodSchemaMapSpanish[EDocumentField.EXPIRATION_DATE],
    }),
  },
};
